import { Layout } from 'components/Layout/Layout';
import { UserContext } from 'context/userContext';
import { useContext, useState } from 'react';
import styles from '../styles/pages/ChangePassword.module.css';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { toast } from 'react-toastify';
import { changePassword } from 'services/api/authService';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import Modal from 'react-modal';
import { ToastMessagesIDs } from 'types/enums';

export const ChangePassword = () => {
  const { loggedUser } = useContext(UserContext);
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { handleSubmit, register, reset } = useForm();
  const [isModalopen, setIsModalOpen] = useState(false);

  if (!loggedUser) {
    navigate('/login');
  }

  const formSubmitHandler = async (data: any) => {
    if (data.newPassword !== data.newPassword2) {
      toast(t('password_missmatch_message'), {
        toastId: ToastMessagesIDs.CHANGE_PASSWORD_ERROR,
      });
      reset();
      return;
    }
    const result = await changePassword(
      data.currentPassword,
      data.newPassword,
      loggedUser
    );
    if (!result) {
      toast(t('wrong_old_password_message'), {
        toastId: ToastMessagesIDs.CHANGE_PASSWORD_ERROR,
      });
      reset();
    } else {
      navigate('/');
    }
  };

  const getFormJSX = () => {
    return (
      <form className={styles.form} onSubmit={handleSubmit(formSubmitHandler)}>
        <label>{t('current_password_label')}:</label>
        <input
          type="password"
          {...register('currentPassword', {
            required: 'This field is required',
          })}
        />

        <div className={styles.newPassword}>
          <label>{t('new_password_label')}:</label>
          {isMobile && (
            <ThemeIcon
              icon="pw-info"
              alt="Info"
              variant="inline"
              className={styles.mobileInfo}
              onClick={() => setIsModalOpen(true)}
            />
          )}
        </div>
        <input
          type="password"
          {...register('newPassword', { required: 'This field is required' })}
        />
        <label>{t('repeat_password_label')}:</label>
        <input
          type="password"
          {...register('newPassword2', { required: 'This field is required' })}
        />
        <button className={styles.submitButton}>
          {t('save_label')}{' '}
          {isMobile && (
            <div className={classNames('checkmark', 'mobile-css-icons')}></div>
          )}
        </button>
      </form>
    );
  };

  const getTipsJSX = () => {
    return (
      <div className={styles.requirementsWrapper}>
        <p>{t('tips_title')}:</p>
        <ul>
          <li>{t('tip_1_label')}</li>
          <li>{t('tip_2_label')}</li>
          <li>{t('tip_3_label')}</li>
        </ul>
      </div>
    );
  };

  return (
    <>
      {!isMobile ? (
        <Layout
          hideSubheader={!loggedUser.has_logged_in_before}
          hideMenu={!loggedUser.has_logged_in_before}
        >
          <div className={styles.wrapper}>
            <div className={styles.container}>
              <p className={styles.title}>
                {loggedUser.has_logged_in_before
                  ? t('change_password_label')
                  : t('initial_password_change_text')}
              </p>
              <div className={styles.infoWrapper}>
                <ThemeIcon icon="info-full" alt="Info" variant="inline" />
                {getTipsJSX()}
              </div>
              {getFormJSX()}
            </div>
          </div>
        </Layout>
      ) : (
        <MobileLayout title={t('change_password_label')}>
          {getFormJSX()}
          <Modal
            isOpen={isModalopen}
            ariaHideApp={false}
            onRequestClose={() => setIsModalOpen(false)}
            contentLabel={t('tips_title')}
            className={styles.modal}
          >
            <div className={styles.modalWrapper}>
              <div
                className={styles.closeButtonWrapper}
                onClick={() => setIsModalOpen(false)}
              >
                <div
                  className={classNames('close-icon', styles.closeIcon)}
                ></div>
              </div>
              {getTipsJSX()}
            </div>
          </Modal>
        </MobileLayout>
      )}
    </>
  );
};
